import "regenerator-runtime/runtime";

import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import PropTypes from "prop-types";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import { verifyToken } from "@ictlearningsciences/pal3-core/api";

import LoadingPage, { ErrorPage } from "./src/components/loading";
import { LoadStatus } from "./src/helpers";
import { store, persistor } from "./src/store";
import { useAppDispatch, useAppSelector } from "./src/store/hooks";
import {
  clearCache,
  loadLessons,
  loadGoals,
  loadDialogue,
  loadSurveys,
  loadStudies,
  loadApp,
} from "./src/store/slices/data";
import { setPageClicked } from "./src/store/slices/dialogue";
import { clearFirstTimeData } from "./src/store/slices/firstTimeData";
import { resetSettings } from "./src/store/slices/settings";
import {
  loadRecords,
  loadUserPlans,
  loadUserTests,
  logout,
  onUpdateRecords,
  saveRecords,
} from "./src/store/slices/userData";

import "./src/styles/layout.css";
import "./src/styles/styles.css";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1b6a9c",
    },
  },
});

function ReduxStoreHydrationComponent({ element }) {
  const dispatch = useAppDispatch();
  const {
    goals,
    goalsLoadState,
    topics,
    lessons,
    resources,
    appLoadState,
    dialogueLoadState,
    studyLoadState,
    surveyLoadState,
  } = useAppSelector((state) => state.data);
  const {
    accessToken,
    activeGoal,
    records,
    unsavedRecords,
    recordLoadState,
    recordSaveState,
    planLoadState,
    userTestLoadState,
  } = useAppSelector((state) => state.userData);
  const { viewedTooltips } = useAppSelector((state) => state.firstTimeData);
  const { character } = useAppSelector((state) => state.settings);

  const [tokenVerified, setTokenVerified] = React.useState(false);
  const [loadMessage, setLoadMessage] = React.useState("");
  const [loadError, setLoadError] = React.useState(false);
  const [hasLoadedData, setLoadedData] = React.useState(false);

  React.useEffect(() => {
    function _init() {
      dispatch(setPageClicked(true));
      document.removeEventListener("click", _init);
    }
    document.addEventListener("click", _init);
    return () => {
      document.removeEventListener("click", _init);
    };
  }, []);

  // load fresh data
  React.useEffect(() => {
    if (typeof lessons === "string") {
      dispatch(clearCache());
    }
    if (typeof viewedTooltips === "string") {
      dispatch(clearFirstTimeData());
    }
    if (typeof character === "string") {
      dispatch(resetSettings());
    }
    const hasLoadedData =
      appLoadState?.endedAt &&
      goalsLoadState?.endedAt &&
      dialogueLoadState?.endedAt &&
      surveyLoadState?.endedAt &&
      studyLoadState?.endedAt;
    if (hasLoadedData) {
      setLoadedData(true);
    } else {
      dispatch(clearCache());
      dispatch(loadApp());
      dispatch(loadGoals());
      dispatch(loadDialogue());
      dispatch(loadSurveys());
      dispatch(loadStudies());
    }
  }, []);

  // load fresh user data
  React.useEffect(() => {
    if (!accessToken || !hasLoadedData) {
      setTokenVerified(false);
      return;
    }
    if (tokenVerified) {
      return;
    }
    verifyToken(accessToken).then((verified) => {
      if (verified) {
        setTokenVerified(true);
        dispatch(loadUserPlans());
        dispatch(loadUserTests());
        dispatch(loadRecords("1971-01-01T00:00:00.000Z"));
      } else {
        setTokenVerified(false);
        dispatch(logout());
      }
    });
  }, [accessToken, hasLoadedData]);

  // load lessons for goal
  React.useEffect(() => {
    if (hasLoadedData && tokenVerified && activeGoal) {
      dispatch(loadLessons(activeGoal.id));
    }
  }, [hasLoadedData, tokenVerified, activeGoal?.id]);

  // update data from learner records
  React.useEffect(() => {
    if (
      tokenVerified &&
      accessToken &&
      recordLoadState.status === LoadStatus.DONE
    ) {
      dispatch(onUpdateRecords({ goals, topics, lessons, resources, records }));
    }
  }, [
    tokenVerified,
    accessToken,
    goals,
    topics,
    lessons,
    resources,
    recordLoadState,
    recordSaveState,
  ]);

  // save learner records
  React.useEffect(() => {
    if (tokenVerified && accessToken && unsavedRecords.length > 0) {
      dispatch(saveRecords());
    }
  }, [unsavedRecords]);

  React.useEffect(() => {
    let msg = "";
    let failed = false;
    if (appLoadState?.status === LoadStatus.IN_PROGRESS) {
      msg += "Loading app...\n";
    }
    if (appLoadState?.status === LoadStatus.FAILED) {
      msg += "Failed to load app\n";
      failed = true;
    }
    if (goalsLoadState?.status === LoadStatus.IN_PROGRESS) {
      msg += "Loading goals...\n";
    }
    if (goalsLoadState?.status === LoadStatus.FAILED) {
      msg += "Failed to load goals\n";
      failed = true;
    }
    if (dialogueLoadState?.status === LoadStatus.IN_PROGRESS) {
      msg += "Loading dialogue...\n";
    }
    if (dialogueLoadState?.status === LoadStatus.FAILED) {
      msg += "Failed to load dialogue\n";
      failed = true;
    }
    if (surveyLoadState?.status === LoadStatus.IN_PROGRESS) {
      msg += "Loading surveys...\n";
    }
    if (surveyLoadState?.status === LoadStatus.FAILED) {
      msg += "Failed to load surveys\n";
      failed = true;
    }
    if (studyLoadState?.status === LoadStatus.IN_PROGRESS) {
      msg += "Loading studies...\n";
    }
    if (studyLoadState?.status === LoadStatus.FAILED) {
      msg += "Failed to load studies\n";
      failed = true;
    }
    if (
      appLoadState?.status === LoadStatus.DONE &&
      goalsLoadState?.status === LoadStatus.DONE &&
      dialogueLoadState?.status === LoadStatus.DONE &&
      surveyLoadState?.status === LoadStatus.DONE &&
      studyLoadState?.status === LoadStatus.DONE
    ) {
      setLoadedData(true);
    }
    setLoadError(failed);
    setLoadMessage(msg);
  }, [
    accessToken,
    appLoadState,
    goalsLoadState,
    dialogueLoadState,
    surveyLoadState,
    studyLoadState,
    recordLoadState,
    planLoadState,
    userTestLoadState,
  ]);

  function reload() {
    if (appLoadState?.status === LoadStatus.FAILED) {
      dispatch(loadApp());
    }
    if (goalsLoadState?.status === LoadStatus.FAILED) {
      dispatch(loadGoals());
    }
    if (dialogueLoadState?.status === LoadStatus.FAILED) {
      dispatch(loadDialogue());
    }
    if (surveyLoadState?.status === LoadStatus.FAILED) {
      dispatch(loadSurveys());
    }
    if (studyLoadState?.status === LoadStatus.FAILED) {
      dispatch(loadStudies());
    }
  }

  return (
    <LoadingPage
      isLoading={!hasLoadedData || (accessToken && !tokenVerified)}
      message={loadMessage}
      error={loadError}
      reload={reload}
    >
      {element}
    </LoadingPage>
  );
}

ReduxStoreHydrationComponent.propTypes = {
  element: PropTypes.node.isRequired,
};

export const wrapRootElement = ({ element }) => {
  const cache = createCache({
    key: "custom",
  });

  return (
    <StyledEngineProvider injectFirst>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorPage
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <ReduxStoreHydrationComponent element={element} />
              </ErrorBoundary>
            </PersistGate>
          </Provider>
        </ThemeProvider>
      </CacheProvider>
    </StyledEngineProvider>
  );
};
